import { Box, ScrollArea, Skeleton, Flex } from '@mantine/core';
import { useRef, useEffect } from 'react';
import { MessageDay } from './MessageDay';
import type { SlackConversationDay } from '../../../api/slackConversation';

export interface ExcerptMapping {
  excerptMessageTsHighlight: Set<string>;
  excerptRefIndexByMessageTs: Map<string, number>;
  excerptTextsByMessageTs: Map<string, string[]>;
  excerptRefsArray: React.MutableRefObject<(HTMLDivElement | null)[]>;
}

function ConversationLoadingSkeleton() {
  return (
    <Box py="md" px="sm">
      {[1, 2, 3].map((i) => (
        <Flex key={i} gap="sm" align="flex-start" mb="md">
          <Skeleton height={36} width={36} radius="xl" />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Skeleton height={14} width={120} mb="xs" />
            <Skeleton height={12} width="80%" />
            <Skeleton height={12} width="60%" mt={4} />
          </Box>
        </Flex>
      ))}
    </Box>
  );
}

interface ConversationViewProps {
  days: SlackConversationDay[];
  centerDate: string;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  excerptMapping?: ExcerptMapping;
}

/** Prevent multiple in-flight fetches per direction: only trigger next/previous when the current one has finished. */
export function ConversationView({
  days,
  centerDate,
  isFetchingNextPage,
  isFetchingPreviousPage,
  hasNextPage,
  hasPreviousPage,
  fetchNextPage,
  fetchPreviousPage,
  excerptMapping,
}: ConversationViewProps) {
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const centerDayRef = useRef<HTMLDivElement>(null);
  const previousFetchTriggeredRef = useRef(false);
  const nextFetchTriggeredRef = useRef(false);
  const hasScrolledToCenterRef = useRef(false);

  useEffect(() => {
    if (hasScrolledToCenterRef.current || !centerDate || days.length === 0) return;
    const hasCenterDay = days.some((d) => d.date === centerDate);
    if (!hasCenterDay) return;
    hasScrolledToCenterRef.current = true;
    requestAnimationFrame(() => {
      centerDayRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  }, [days, centerDate]);

  useEffect(() => {
    if (!isFetchingPreviousPage) previousFetchTriggeredRef.current = false;
  }, [isFetchingPreviousPage]);

  useEffect(() => {
    if (!isFetchingNextPage) nextFetchTriggeredRef.current = false;
  }, [isFetchingNextPage]);

  useEffect(() => {
    const root = scrollViewportRef.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          !entry.isIntersecting ||
          !hasPreviousPage ||
          isFetchingPreviousPage ||
          previousFetchTriggeredRef.current
        ) {
          return;
        }
        previousFetchTriggeredRef.current = true;
        fetchPreviousPage();
      },
      { root, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasPreviousPage, isFetchingPreviousPage, fetchPreviousPage]);

  useEffect(() => {
    const root = scrollViewportRef.current;
    const sentinel = bottomSentinelRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          !entry.isIntersecting ||
          !hasNextPage ||
          isFetchingNextPage ||
          nextFetchTriggeredRef.current
        ) {
          return;
        }
        nextFetchTriggeredRef.current = true;
        fetchNextPage();
      },
      { root, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <ScrollArea
      viewportRef={scrollViewportRef}
      style={{ flex: 1, height: '100%' }}
      type="auto"
      offsetScrollbars
    >
      <Box p={0}>
        <div ref={topSentinelRef} style={{ height: 1, visibility: 'hidden' }} />
        {isFetchingPreviousPage && <ConversationLoadingSkeleton />}
        {days.map((day) => (
          <MessageDay
            key={day.date}
            ref={day.date === centerDate ? centerDayRef : undefined}
            date={day.date}
            messages={day.messages}
            channelName={day.channelName}
            excerptMessageTsHighlight={excerptMapping?.excerptMessageTsHighlight}
            excerptRefIndexByMessageTs={excerptMapping?.excerptRefIndexByMessageTs}
            excerptTextsByMessageTs={excerptMapping?.excerptTextsByMessageTs}
            excerptRefsArray={excerptMapping?.excerptRefsArray}
          />
        ))}
        {isFetchingNextPage && <ConversationLoadingSkeleton />}
        <div ref={bottomSentinelRef} style={{ height: 1, visibility: 'hidden' }} />
      </Box>
    </ScrollArea>
  );
}
